import { useCallback, useEffect, useRef, useState } from 'react'

import { useLocation } from 'react-router'

import { Sso, SsoStatus, SsoType } from '@tribeplatform/gql-client/types'
import {
  useAuthToken,
  useLoginWithSso,
  useSsos,
} from '@tribeplatform/react-sdk/hooks'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Divider } from '@tribeplatform/react-ui-kit/Divider'

import { useQuery } from '../../lib/useQuery'
import { getSsoCallbackUrl } from '../../utils/sso'
import { SsoIcon } from './SsoIcons'

export const Ssos = ({
  hasDivider,
  invitationLinkId,
  invitationToken,
}: {
  hasDivider: boolean
  invitationLinkId?: string
  invitationToken?: string
}) => {
  const location = useLocation()
  const query = useQuery()
  const redirectedAutomatically = useRef(query?.auto_login === 'false')
  const [loadingType, setLoadingType] = useState<SsoType>()
  const {
    data: { network },
  } = useAuthToken()
  const { data: enabledSsos } = useSsos({
    variables: { status: SsoStatus.enable },
  })
  const { login } = useLoginWithSso()
  const isLoginPage = location.pathname.includes('/auth/login')

  const onJwtClick = useCallback(
    (jwtSso: Sso) => {
      const url =
        (isLoginPage ? jwtSso.authorizationUrl : jwtSso.signUpUrl) ||
        jwtSso.authorizationUrl ||
        jwtSso.signUpUrl
      if (!url) return

      window.location.href = url
    },
    [isLoginPage],
  )
  const onClick = useCallback(
    async type => {
      setLoadingType(type)
      const callbackUrl = getSsoCallbackUrl(
        type,
        network.domain,
        query.redirect,
      )

      const response = await login({
        input: {
          type,
          callbackUrl,
          invitationLinkId,
          invitationToken,
        },
      }).catch(e => {
        setLoadingType(undefined)
        throw e
      })

      window.location.href = response?.url
    },
    [login, network.domain, invitationLinkId, invitationToken, query.redirect],
  )
  const enabledVisibleSsos = enabledSsos?.filter(
    sso => sso.type !== SsoType.jwt,
  )
  const jwtProvider = enabledSsos?.find(sso => sso.type === SsoType.jwt)
  const jwtActive: boolean = isLoginPage
    ? !!jwtProvider?.authorizationUrl
    : !!jwtProvider?.signUpUrl

  useEffect(() => {
    const oauth2 = enabledVisibleSsos?.find(sso => sso.type === SsoType.oauth2)
    if (
      !redirectedAutomatically.current &&
      network.hideDefaultAuthenticationForm
    ) {
      // failsafe for oauth2
      if (enabledVisibleSsos?.length === 1 && oauth2) {
        redirectedAutomatically.current = true
        onClick(SsoType.oauth2)
      }
      // failsafe for jwt
      if (enabledVisibleSsos?.length === 0 && jwtProvider) {
        redirectedAutomatically.current = true
        onJwtClick(jwtProvider)
      }
    }
  }, [
    enabledVisibleSsos,
    jwtProvider,
    onClick,
    onJwtClick,
    network.hideDefaultAuthenticationForm,
  ])

  if (!enabledVisibleSsos?.length && !jwtActive) return null

  return (
    <>
      {hasDivider ? <Divider>Or continue with</Divider> : null}
      <div className="grid grid-cols-1 gap-3">
        {enabledVisibleSsos.map(({ name, buttonText, type }) => (
          <Button
            href="#"
            onClick={() => onClick(type)}
            className="justify-center"
            key={type}
            variant="outline"
            size="lg"
            loading={type === loadingType}
            leadingIcon={
              SsoIcon[type] && <span className="mr-2">{SsoIcon[type]}</span>
            }
          >
            {buttonText || name}
          </Button>
        ))}
        {jwtActive && (
          <Button
            href="#"
            onClick={() => {
              onJwtClick(jwtProvider)
            }}
            className="justify-center"
            key={jwtProvider.type}
            variant="outline"
            size="lg"
          >
            {jwtProvider.buttonText || 'Login with JWT'}
          </Button>
        )}
      </div>
    </>
  )
}
